import {
  Globe01Icon,
  Link01Icon,
  Upload01Icon,
} from '@langchain/untitled-ui-icons';
import { Typography } from '@mui/joy';
import * as Popover from '@radix-ui/react-popover';

import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { SWRResponse } from 'swr';

import { useCopy } from '@/components/CopyButton/useCopy';
import { Button, IconButton, Spinner, Tooltip } from '@/design-system';
import { usePublicSharingDisabled } from '@/hooks/usePublicSharingDisabled';
import { useShareRunMutation, useUnshareRunMutation } from '@/hooks/useSwr';
import { GetDatasetShareBody, RunSchema } from '@/types/schema';
import { appPublicPath, appRunPath } from '@/utils/constants';

type ShareBody = Pick<
  SWRResponse<RunSchema> | SWRResponse<GetDatasetShareBody>,
  'data' | 'isLoading'
>;

type SharePanelProps = {
  share: ShareBody;
  shareMutation: ReturnType<typeof useShareRunMutation>;
  unshareMutation: ReturnType<typeof useUnshareRunMutation>;
  url: string;
  readableUrl: string;
  copied: boolean;
  onCopy: () => void;
  objectName?: string;
  disclaimer?: React.ReactNode;
  variant?: 'icon' | 'button' | 'custom';
  customButton?: (icon: React.ReactNode, isPublic: boolean) => React.ReactNode;
  bypassPortal?: boolean;
};

export function SharePanel({
  share,
  shareMutation,
  unshareMutation,
  url,
  readableUrl,
  copied,
  onCopy,
  disclaimer,
  customButton,
  variant = 'icon',
  bypassPortal = false,
}: SharePanelProps) {
  const [open, setOpen] = useState(false);
  const publicSharingDisabled = usePublicSharingDisabled();
  if (variant === 'custom' && !customButton) {
    throw new Error('customButton is required when variant is custom');
  }

  const shouldOpen = Boolean(shareMutation.data);
  useEffect(() => {
    if (shouldOpen) {
      setOpen(true);
    }
  }, [shouldOpen]);

  const popoverContent = (
    <Popover.Content
      sideOffset={5}
      className="relative z-[1000]"
      side="left"
      align="start"
    >
      <div className="flex max-w-[420px] flex-col gap-2 rounded-xl border border-secondary bg-popover p-4 shadow-lg">
        {disclaimer}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 overflow-hidden text-brand-green-500">
            <Link01Icon className="h-4 w-4 shrink-0" />
            <Link
              className="block overflow-hidden text-ellipsis whitespace-nowrap"
              to={url}
            >
              {readableUrl}
            </Link>
          </div>

          <Button
            size="sm"
            variant="outlined"
            color="secondary"
            autoFocus
            onClick={onCopy}
            data-testid="copy-link-button"
          >
            {copied ? 'Copied' : 'Copy'}
          </Button>

          <Button
            size="sm"
            variant="outlined"
            color="error"
            loading={unshareMutation.isMutating}
            onClick={() => unshareMutation.trigger()}
          >
            Unshare
          </Button>
        </div>
      </div>
    </Popover.Content>
  );

  const content = (
    <>
      {share.data?.share_token != null && (
        <Popover.Root open={open} onOpenChange={setOpen}>
          {variant === 'icon' && (
            <Popover.Trigger asChild>
              <IconButton
                size="sm"
                color="secondary"
                variant="outlined"
                disabled={unshareMutation.isMutating}
                icon={Globe01Icon}
                label="Public"
                tooltipProps={{
                  side: 'bottom',
                }}
              />
            </Popover.Trigger>
          )}

          {variant === 'button' && (
            <Popover.Trigger asChild>
              <Button
                size="sm"
                color="secondary"
                variant="outlined"
                leftDecorator={Globe01Icon}
                disabled={unshareMutation.isMutating}
              >
                Public
              </Button>
            </Popover.Trigger>
          )}

          {variant === 'custom' && customButton && (
            <Popover.Trigger asChild>
              {customButton(<Globe01Icon className="h-4 w-4" />, true)}
            </Popover.Trigger>
          )}
          {bypassPortal ? (
            popoverContent
          ) : (
            <Popover.Portal>{popoverContent}</Popover.Portal>
          )}
        </Popover.Root>
      )}

      {share.data?.share_token == null && (
        <Fragment>
          {variant === 'icon' && (
            <IconButton
              size="sm"
              color="secondary"
              variant="outlined"
              onClick={() => shareMutation.trigger()}
              label={
                publicSharingDisabled
                  ? 'Public sharing disabled, contact your organization administrator'
                  : 'Share'
              }
              tooltipProps={{
                side: 'bottom',
              }}
              icon={Upload01Icon}
              loading={shareMutation.isMutating}
              disabled={publicSharingDisabled}
            />
          )}

          {variant === 'button' && (
            <Tooltip
              title={
                publicSharingDisabled
                  ? 'Public sharing disabled, contact your organization administrator'
                  : 'Share'
              }
              side="bottom"
            >
              <Button
                size="sm"
                color="secondary"
                variant="outlined"
                leftDecorator={Upload01Icon}
                loading={shareMutation.isMutating}
                disabled={publicSharingDisabled}
                onClick={() => shareMutation.trigger()}
              >
                Share
              </Button>
            </Tooltip>
          )}

          {variant === 'custom' && customButton && (
            <div
              onClick={() => !publicSharingDisabled && shareMutation.trigger()}
            >
              {customButton(
                shareMutation.isMutating ? (
                  <Spinner />
                ) : (
                  <Upload01Icon className="h-4 w-4" />
                ),
                false
              )}
            </div>
          )}
        </Fragment>
      )}
    </>
  );

  return content;
}

export function ShareAction(props: {
  run: RunSchema;
  variant?: 'icon' | 'button';
}) {
  const shareMutation = useShareRunMutation(props.run.id);
  const unshareMutation = useUnshareRunMutation(props.run.id);

  const url = `/${appPublicPath}/${props.run.share_token}/${appRunPath}`;
  const readableUrl = `${props.run.share_token}/${appRunPath}`;

  const { copied, onCopy } = useCopy({ copy: window.location.origin + url });

  return (
    <SharePanel
      share={useMemo(
        () => ({ data: props.run, isLoading: false }),
        [props.run]
      )}
      shareMutation={shareMutation}
      unshareMutation={unshareMutation}
      variant={props.variant}
      url={url}
      readableUrl={readableUrl}
      copied={copied}
      onCopy={onCopy}
      disclaimer={
        <div className="flex flex-col gap-1">
          <Typography fontWeight={600}>Run Shared</Typography>
          <Typography color="neutral" fontSize="md">
            Anyone with the link below can view this run and the feedback
            associated with the run.
          </Typography>
        </div>
      }
    />
  );
}
